import authService from './auth.service';
import type { SecretRevealReason } from '../types/secret.types';

const API_URL = '/api/v1';

export interface DictSummary {
  code: string;
  title: string;
  is_custom?: boolean;
  has_entries?: boolean;
}

export interface DictItem {
  id: number;
  code: string;
  name: string;
}

export interface DictAction {
  id: number;
  code: string;
  name: string;
  group?: string;
}

export interface DictEntryRecord {
  id: number;
  dict_code: string;
  dict_name: string;
  subject_type: string;
  subject_id: number;
  item_id: number;
  item_code: string;
  item_name: string;
  is_active: boolean;
  comment: string | null;
  created_by_user_id: number | null;
  created_by_name: string | null;
  created_at: string;
  deactivated_by_user_id: number | null;
  deactivated_by_name: string | null;
  deactivated_at: string | null;
}

class DictsService {
  // Кеш для словарей (предотвращает дублирование запросов)
  private cache: Map<string, { data: unknown; timestamp: number }> = new Map();
  private pendingRequests: Map<string, Promise<unknown>> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 минут

  private async cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    // Проверяем кеш
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data as T;
    }

    // Проверяем, есть ли уже запрос в процессе
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    // Создаём новый запрос
    const request = fetcher().then((data) => {
      this.cache.set(key, { data, timestamp: Date.now() });
      this.pendingRequests.delete(key);
      return data;
    }).catch((error) => {
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  async listDicts(): Promise<DictSummary[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить список справочников');
    }
    const data = await res.json();
    return Array.isArray(data) ? data as DictSummary[] : [];
  }

  async listDictsFiltered(hasEntries?: boolean): Promise<DictSummary[]> {
    const params = hasEntries !== undefined ? `?has_entries=${hasEntries}` : '';
    const cacheKey = `dicts_filtered_${hasEntries ?? 'all'}`;
    return this.cachedFetch<DictSummary[]>(cacheKey, async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/dicts/${params}`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить справочники');
      }
      const data = await res.json();
      return Array.isArray(data) ? data as DictSummary[] : [];
    });
  }

  /** @deprecated используйте listDictsFiltered */
  async listDictsWithEntries(): Promise<DictSummary[]> {
    return this.listDictsFiltered(false);
  }

  async listDictItems(code: string): Promise<DictItem[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/${encodeURIComponent(code)}/`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить элементы справочника');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as DictItem[]) : [];
  }

  async listDictItemsParams(
    code: string,
    params?: Record<string, string | number | boolean | null | undefined>
  ): Promise<DictItem[]> {
    const usp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      usp.set(k, String(v));
    });
    const query = usp.toString();
    const url = `${API_URL}/dicts/${encodeURIComponent(code)}/` + (query ? `?${query}` : '');
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      throw new Error('Не удалось загрузить элементы справочника');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as DictItem[]) : [];
  }

  async createDictItem(dictCode: string, payload: { code: string; name: string }): Promise<DictItem> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/${encodeURIComponent(dictCode)}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать элемент');
    }
    return (await res.json()) as DictItem;
  }

  async updateDictItem(dictCode: string, id: number, payload: { code: string; name: string }): Promise<DictItem> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/${encodeURIComponent(dictCode)}/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить элемент');
    }
    return (await res.json()) as DictItem;
  }

  async deleteDictItem(dictCode: string, id: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/${encodeURIComponent(dictCode)}/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить элемент');
    }
  }

  async getStages(serviceType: string): Promise<DictItem[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/stages?service_types=${encodeURIComponent(serviceType)}`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить этапы');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as DictItem[]) : [];
  }

  async getLeadStages(): Promise<DictItem[]> {
    return this.cachedFetch('lead_stages', async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/stages?service_types=leads`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить этапы заявок');
      }
      const data = await res.json();
      return Array.isArray(data) ? (data as DictItem[]) : [];
    });
  }

  async getLeadStatuses(): Promise<DictItem[]> {
    return this.cachedFetch('lead_statuses', async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/dicts/lead_statuses/`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить статусы заявок');
      }
      const data = await res.json();
      return Array.isArray(data) ? (data as DictItem[]) : [];
    });
  }

  async getPhoneTypes(): Promise<DictItem[]> {
    return this.cachedFetch('phone_types', async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/dicts/phone_types/`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить типы телефонов');
      }
      const data = await res.json();
      return Array.isArray(data) ? (data as DictItem[]) : [];
    });
  }

  async getPhoneStatuses(): Promise<DictItem[]> {
    return this.cachedFetch('phone_statuses', async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/dicts/phone_statuses/`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить статусы телефонов');
      }
      const data = await res.json();
      return Array.isArray(data) ? (data as DictItem[]) : [];
    });
  }

  async getActionsByGroup(group: string, params?: { is_bulk?: boolean; is_single?: boolean }): Promise<DictAction[]> {
    const usp = new URLSearchParams();
    usp.set('group', group);
    if (params?.is_bulk !== undefined) {
      usp.set('is_bulk', String(params.is_bulk));
    }
    if (params?.is_single !== undefined) {
      usp.set('is_single', String(params.is_single));
    }
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/actions/?${usp.toString()}`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить действия');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as DictAction[]) : [];
  }

  async getSecretRevealReasons(): Promise<SecretRevealReason[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/secret_reveal_reasons/`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить причины раскрытия');
    }
    const data = await res.json();
    return Array.isArray(data) ? (data as SecretRevealReason[]) : [];
  }

  async getAgreementStatuses(): Promise<DictItem[]> {
    return this.cachedFetch('agreement_statuses', async () => {
      const res = await authService.fetchWithAuth(`${API_URL}/dicts/agreement_statuses/`);
      if (!res.ok) {
        throw new Error('Не удалось загрузить статусы договоров');
      }
      const data = await res.json();
      return Array.isArray(data) ? (data as DictItem[]) : [];
    });
  }

  async createCustomDict(payload: { code: string; name: string; has_entries?: boolean }): Promise<{ id: number; code: string; name: string; has_entries: boolean }> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/custom/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать справочник');
    }
    return await res.json();
  }

  async deleteCustomDict(code: string): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/dicts/custom/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить справочник');
    }
  }

  // ===================== Dict Entries (таблицы dict_{code}) =====================

  async createDictEntry(
    dictCode: string,
    data: { item_id: number; subject_type: string; subject_id: number; comment?: string }
  ): Promise<Record<string, unknown>> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/dicts/${encodeURIComponent(dictCode)}/entries/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать запись');
    }
    return res.json();
  }

  async listDictEntries(
    dictCode: string,
    params?: {
      subject_type?: string;
      subject_id?: number;
      active_only?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<Record<string, unknown>[]> {
    const usp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.set(k, String(v));
      });
    }
    const query = usp.toString();
    const url = `${API_URL}/dicts/${encodeURIComponent(dictCode)}/entries/${query ? `?${query}` : ''}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить записи');
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }

  async listAllEntriesBySubject(
    subjectType: string,
    subjectId: number
  ): Promise<DictEntryRecord[]> {
    const url = `${API_URL}/dicts/entries/by-subject?subject_type=${encodeURIComponent(subjectType)}&subject_id=${subjectId}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить записи справочников');
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }
}

export default new DictsService();
